// Attendance tabs (Mercedes, Nov 3, 2026): Points, Missed punches, Holidays, My alerts
// and Team. Every rule about who may see or decide what is on the server
// (lib/attendance-team-db.js); these only show what comes back.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { attendance } from '../../services/api';
import { Msg, box, btn, btn2, input, lbl, th, td, left, muted, addDays, fmtDay, fmtShort, fmtWhen, clock, pts, todayEt } from './shared';

// ── Points ───────────────────────────────────────────────────────────────────
export function Points({ me }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [notes, setNotes] = useState({});
  const [showAll, setShowAll] = useState(false);
  const load = useCallback(() => attendance('points').then(setData, (e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const decide = async (p, decision) => {
    setErr('');
    try { await attendance('point-decide', { id: p.id, decision, note: notes[p.id] || '' }); load(); } catch (e) { setErr(e.message); }
  };
  if (!data) return <><Msg err={err} /><p>Loading points…</p></>;
  const pending = data.points.filter((p) => p.status === 'pending');
  const decided = data.points.filter((p) => p.status !== 'pending');
  const totals = Object.entries(data.totals).sort((a, b) => b[1].total - a[1].total || a[1].name.localeCompare(b[1].name));

  return (
    <div>
      <p style={{ ...muted, maxWidth: 820 }}>
        1 point for a call-out with no sick time; 1/2 point for coming in or leaving {me.pointRules.lateMin / 60} or more hours off schedule.
        Nothing counts until it is approved, with a note. A point falls off one year after the day it was earned. {data.termination} points is termination.
        Points stay on this page and never go in an email. Flags start {fmtDay(me.pointRules.from)}.
      </p>
      <Msg err={err} />

      <h3 style={{ fontSize: 15, margin: '10px 0 6px' }}>Current totals</h3>
      <div style={{ ...box, marginBottom: 16, maxWidth: 520 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr><th style={{ ...th, ...left }}>Person</th><th style={th}>Points</th><th style={th}>Pending</th></tr></thead>
          <tbody>
            {totals.map(([k, t]) => {
              const hot = t.total >= 5;
              return (
                <tr key={k} style={{ borderTop: '1px solid #eef0f2', background: hot ? '#fdeaea' : undefined }}>
                  <td style={{ ...td, ...left, fontWeight: 600 }}>{t.name}{hot ? <span style={{ color: '#b42318' }}> (at {pts(t.total)}, talk before 6)</span> : null}</td>
                  <td style={{ ...td, fontWeight: 700, color: hot ? '#b42318' : undefined }}>{pts(t.total)}</td>
                  <td style={td}>{t.pending || ''}</td>
                </tr>
              );
            })}
            {!totals.length && <tr><td style={{ ...td, ...left }} colSpan={3}>No points.</td></tr>}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontSize: 15, margin: '10px 0 6px' }}>Waiting for a decision ({pending.length})</h3>
      {!pending.length && <p style={muted}>Nothing waiting.</p>}
      {pending.map((p) => (
        <div key={p.id} style={{ ...box, padding: 10, marginBottom: 8, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ minWidth: 240 }}><b>{p.person_name}</b>, {fmtShort(p.day)}<div style={muted}>{pts(p.points)} point{p.points === 1 ? '' : 's'}: {p.why}</div></div>
          {p.canDecide ? (
            <>
              <input value={notes[p.id] || ''} onChange={(e) => setNotes({ ...notes, [p.id]: e.target.value })} placeholder="Note explaining why" style={{ ...input, flex: '1 1 220px' }} />
              <button type="button" style={btn} disabled={!(notes[p.id] || '').trim()} onClick={() => decide(p, 'approved')}>Approve</button>
              <button type="button" style={btn2} disabled={!(notes[p.id] || '').trim()} onClick={() => decide(p, 'denied')}>Deny</button>
            </>
          ) : <span style={muted}>Waiting for {p.approver || 'their approver'}.</span>}
        </div>
      ))}

      <h3 style={{ fontSize: 15, margin: '16px 0 6px' }}>Decided</h3>
      <label style={{ fontSize: 13 }}><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Include points that have fallen off</label>
      <div style={{ ...box, marginTop: 6 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr>
            <th style={{ ...th, ...left }}>Person</th><th style={{ ...th, ...left }}>Day</th><th style={th}>Points</th><th style={{ ...th, ...left }}>Why</th>
            <th style={{ ...th, ...left }}>Decision</th><th style={{ ...th, ...left }}>Falls off</th><th />
          </tr></thead>
          <tbody>
            {decided.filter((p) => showAll || p.status !== 'approved' || p.active).map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid #eef0f2', color: p.status === 'denied' || !p.active ? '#9ca3af' : undefined }}>
                <td style={{ ...td, ...left }}>{p.person_name}</td>
                <td style={{ ...td, ...left }}>{fmtDay(p.day)}</td>
                <td style={td}>{pts(p.points)}</td>
                <td style={{ ...td, ...left, whiteSpace: 'normal' }}>{p.why}</td>
                <td style={{ ...td, ...left, whiteSpace: 'normal' }}>{p.status === 'approved' ? 'Approved' : 'Denied'} by {p.decided_by}, {fmtWhen(p.decided_at)}: {p.decision_note}</td>
                <td style={{ ...td, ...left }}>{p.status === 'approved' ? fmtDay(p.fallsOff) : ''}</td>
                <td style={td}>{p.canDecide && <button type="button" style={{ ...btn2, padding: '2px 6px', fontSize: 11 }} onClick={() => decide(p, 'pending')}>Reopen</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Missed punches ───────────────────────────────────────────────────────────
// The punches the clock shows missing over the last two weeks, a form to enter the
// right time (it goes to Mercedes's queue), and the queue. Nothing is written back to
// TimeClock Plus; Mercedes fixes it there herself.
export function Punches({ me }) {
  const [grid, setGrid] = useState(null);
  const [queue, setQueue] = useState(null);
  const [form, setForm] = useState({});
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const today = todayEt();
  const load = useCallback(async () => {
    setErr('');
    try {
      const [g, q] = await Promise.all([attendance('grid', { from: addDays(today, -14), to: today }), attendance('punches')]);
      setGrid(g); setQueue(q);
    } catch (e) { setErr(e.message); }
  }, [today]);
  useEffect(() => { load(); }, [load]);

  const found = useMemo(() => {
    if (!grid || !queue) return [];
    const done = new Set(queue.fixes.map((f) => `${f.person_key}|${f.day}|${f.kind}`));
    const out = [];
    for (const p of grid.people) for (const d of p.days) for (const m of d.missedClock || []) {
      if (!done.has(`${p.key}|${d.date}|${m.kind}`)) out.push({ p, d, m, id: `${p.key}|${d.date}|${m.kind}` });
    }
    return out.sort((a, b) => (a.d.date < b.d.date ? 1 : -1));
  }, [grid, queue]);

  const submit = async (item, kindOverride) => {
    const f = form[item.id] || {};
    setErr(''); setOk('');
    try {
      await attendance('punch-fix', { person_key: item.p.key, day: item.d.date, kind: kindOverride || f.kind || item.m.kind, correct_time: f.time || '', reason: f.reason || '' });
      setOk(`Sent to Mercedes: ${item.p.name}, ${fmtShort(item.d.date)}.`);
      load();
    } catch (e) { setErr(e.message); }
  };
  const done = async (f, undo) => {
    try { await attendance('punch-done', { id: f.id, undo }); load(); } catch (e) { setErr(e.message); }
  };

  return (
    <div>
      <p style={{ ...muted, maxWidth: 820 }}>
        Punches the time clock shows missing in the last two weeks. Enter the right time and it goes into Mercedes's queue; she fixes it in TimeClock Plus.
        If she types the reason in the punch's note there, it shows up here and in the monthly report.
      </p>
      <Msg err={err} ok={ok} />
      {!grid && <p>Reading the time clock…</p>}
      {grid && !found.length && <p style={muted}>No missed punches waiting.</p>}
      {found.map((item) => {
        const f = form[item.id] || {};
        return (
          <div key={item.id} style={{ ...box, padding: 10, marginBottom: 8 }}>
            <div><b>{item.p.name}</b>, {fmtShort(item.d.date)}: {item.m.text}</div>
            <div style={muted}>Punches: {item.d.work.map((w) => `${clock(w.in)} to ${w.out ? clock(w.out) : '?'}`).join(', ')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 6 }}>
              <label style={lbl}>Which punch
                <select value={f.kind || item.m.kind} onChange={(e) => setForm({ ...form, [item.id]: { ...f, kind: e.target.value } })} style={input}>
                  {Object.entries(PUNCH_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
              <label style={lbl}>Right time<input type="time" value={f.time || ''} onChange={(e) => setForm({ ...form, [item.id]: { ...f, time: e.target.value } })} style={input} /></label>
              <label style={{ ...lbl, flex: '1 1 220px' }}>Reason<input value={f.reason || ''} onChange={(e) => setForm({ ...form, [item.id]: { ...f, reason: e.target.value } })} style={input} placeholder="Forgot to clock back in after lunch" /></label>
              <button type="button" style={btn} disabled={!f.time} onClick={() => submit(item)}>Send to Mercedes</button>
            </div>
          </div>
        );
      })}

      <h3 style={{ fontSize: 15, margin: '18px 0 6px' }}>Queue</h3>
      {queue && !queue.fixes.length && <p style={muted}>Nothing in the queue.</p>}
      {queue && queue.fixes.length > 0 && (
        <div style={box}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>
              <th style={{ ...th, ...left }}>Person</th><th style={{ ...th, ...left }}>Day</th><th style={{ ...th, ...left }}>Punch</th><th style={th}>Right time</th>
              <th style={{ ...th, ...left }}>Reason</th><th style={{ ...th, ...left }}>Entered</th><th style={{ ...th, ...left }}>Status</th>
            </tr></thead>
            <tbody>
              {queue.fixes.map((f) => (
                <tr key={f.id} style={{ borderTop: '1px solid #eef0f2', color: f.status === 'done' ? '#9ca3af' : undefined }}>
                  <td style={{ ...td, ...left, fontWeight: 600 }}>{f.person_name}</td>
                  <td style={{ ...td, ...left }}>{fmtShort(f.day)}</td>
                  <td style={{ ...td, ...left }}>{PUNCH_KINDS[f.kind] || f.kind}</td>
                  <td style={td}>{clock(f.correct_time)}</td>
                  <td style={{ ...td, ...left, whiteSpace: 'normal' }}>{f.reason}</td>
                  <td style={{ ...td, ...left }}>{f.entered_by}, {fmtWhen(f.entered_at)}</td>
                  <td style={{ ...td, ...left }}>
                    {f.status === 'done' ? `Fixed by ${f.done_by}` : 'Waiting'}
                    {queue.canClose && <button type="button" style={{ ...btn2, padding: '2px 6px', fontSize: 11, marginLeft: 6 }} onClick={() => done(f, f.status === 'done')}>{f.status === 'done' ? 'Reopen' : 'Fixed in TCP'}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
const PUNCH_KINDS = {
  missed_clock_in: 'Clock in', missed_clock_out: 'Clock out',
  missed_lunch_out: 'Out for lunch', missed_lunch_in: 'In from lunch',
  missed_break_out: 'Out for break', missed_break_in: 'In from break',
};

// ── Holidays and holiday pay ─────────────────────────────────────────────────
export function Holidays() {
  const [year, setYear] = useState(Number(todayEt().slice(0, 4)));
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setErr(''); setBusy(true);
    try { setData(await attendance('holidays', { year })); } catch (e) { setErr(e.message); }
    setBusy(false);
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { await attendance('holiday-save', { holiday: edit }); setEdit(null); load(); } catch (e) { setErr(e.message); }
  };
  const del = async (day) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this holiday?')) return;
    try { await attendance('holiday-delete', { day }); load(); } catch (e) { setErr(e.message); }
  };
  const reviewed = async () => { try { await attendance('holidays-reviewed', { year }); load(); } catch (e) { setErr(e.message); } };

  return (
    <div>
      <p style={{ ...muted, maxWidth: 860 }}>
        Holiday pay only when the holiday is on a business day (or the business day you mark as observed for a weekend holiday). Full-time, year-round people only;
        seasonals never. They must work the business day before and after; time off counts if it was in TimeClock Plus ahead of time, and an unscheduled call-out on either day disqualifies.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <button type="button" style={btn2} onClick={() => setYear(year - 1)}>{year - 1}</button>
        <b style={{ fontSize: 16 }}>{year}</b>
        <button type="button" style={btn2} onClick={() => setYear(year + 1)}>{year + 1}</button>
        <button type="button" style={btn} onClick={() => setEdit({ day: '', name: '', paid: true, observed_for: '' })}>Add a day</button>
        {data && <button type="button" style={btn2} onClick={reviewed}>Mark {year} reviewed</button>}
        {busy && <span style={muted}>Checking the time clock…</span>}
      </div>
      <Msg err={err} />
      {edit && (
        <div style={{ ...box, padding: 10, marginBottom: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={lbl}>Day<input type="date" value={edit.day} onChange={(e) => setEdit({ ...edit, day: e.target.value })} style={input} /></label>
          <label style={lbl}>Name<input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} style={input} /></label>
          <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center' }}><input type="checkbox" checked={!!edit.paid} onChange={(e) => setEdit({ ...edit, paid: e.target.checked })} /> Paid</label>
          <label style={lbl}>Observed for (a weekend holiday)<input type="date" value={edit.observed_for || ''} onChange={(e) => setEdit({ ...edit, observed_for: e.target.value })} style={input} /></label>
          <button type="button" style={btn} onClick={save}>Save</button>
          <button type="button" style={btn2} onClick={() => setEdit(null)}>Cancel</button>
        </div>
      )}
      {data && (
        <div style={{ ...box, marginBottom: 16, maxWidth: 760 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={{ ...th, ...left }}>Day</th><th style={{ ...th, ...left }}>Holiday</th><th style={{ ...th, ...left }}>Paid</th><th style={{ ...th, ...left }}>Reviewed</th><th /></tr></thead>
            <tbody>
              {data.holidays.map((h) => (
                <tr key={h.day} style={{ borderTop: '1px solid #eef0f2', color: h.paid ? undefined : '#9ca3af' }}>
                  <td style={{ ...td, ...left }}>{fmtShort(h.day)}, {h.day.slice(0, 4)}</td>
                  <td style={{ ...td, ...left }}>{h.name}{h.observed_for ? ` (observed for ${fmtShort(h.observed_for)})` : ''}</td>
                  <td style={{ ...td, ...left }}>{h.paid ? 'Paid' : 'Not paid (weekend)'}</td>
                  <td style={{ ...td, ...left }}>{h.reviewed_by ? `${h.reviewed_by}, ${fmtWhen(h.reviewed_at)}` : ''}</td>
                  <td style={td}>
                    <button type="button" style={{ ...btn2, padding: '2px 6px', fontSize: 11 }} onClick={() => setEdit({ ...h, was: h.day, observed_for: h.observed_for || '' })}>Edit</button>{' '}
                    <button type="button" style={{ ...btn2, padding: '2px 6px', fontSize: 11 }} onClick={() => del(h.day)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && Object.entries(data.status).map(([day, list]) => {
        const h = data.holidays.find((x) => x.day === day);
        return (
          <div key={day} style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, margin: '6px 0' }}>Holiday pay: {h ? h.name : ''}, {fmtShort(day)}</h3>
            <div style={{ ...box, maxWidth: 860 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr><th style={{ ...th, ...left }}>Person</th><th style={{ ...th, ...left }}>Gets holiday pay</th><th style={{ ...th, ...left }}>Why</th></tr></thead>
                <tbody>
                  {list.map((r) => (
                    <tr key={r.key} style={{ borderTop: '1px solid #eef0f2' }}>
                      <td style={{ ...td, ...left, fontWeight: 600 }}>{r.name}</td>
                      <td style={{ ...td, ...left, color: r.status === 'yes' ? '#1c7c43' : r.status === 'no' ? '#b42318' : '#6b7280', fontWeight: 700 }}>
                        {{ yes: 'Yes', no: 'No', salaried: 'Salaried', pending: 'Not yet known' }[r.status]}
                      </td>
                      <td style={{ ...td, ...left, whiteSpace: 'normal' }}>{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── My alerts ────────────────────────────────────────────────────────────────
export function Alerts({ me, onSaved }) {
  const [subs, setSubs] = useState(Object.fromEntries(me.alerts.map((a) => [a.key, a.enabled])));
  const [ok, setOk] = useState('');
  const [err, setErr] = useState('');
  const save = async () => {
    setOk(''); setErr('');
    try { await attendance('alerts-save', { alerts: subs }); setOk('Saved.'); onSaved(); } catch (e) { setErr(e.message); }
  };
  const teamNames = me.access.see.filter((t) => t !== 'other').map((t) => me.teams[t]).join(' and ');
  return (
    <div style={{ maxWidth: 680 }}>
      <p style={muted}>Pick the emails you want. They cover {teamNames || 'your team'} only, and you get nothing you did not tick.</p>
      <Msg err={err} ok={ok} />
      {me.alerts.map((a) => (
        <label key={a.key} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', fontSize: 14 }}>
          <input type="checkbox" checked={!!subs[a.key]} onChange={(e) => setSubs({ ...subs, [a.key]: e.target.checked })} /> {a.label}
        </label>
      ))}
      <button type="button" style={{ ...btn, marginTop: 8 }} onClick={save}>Save my alerts</button>
    </div>
  );
}

// ── Team roster (Mercedes and Greg) ──────────────────────────────────────────
export function TeamRoster({ me }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => attendance('team').then(setData, (e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setErr('');
    try { await attendance('team-save', { member: edit }); setEdit(null); load(); } catch (e) { setErr(e.message); }
  };
  const remove = async (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Take ${m.person_name} off the teams?`)) return;
    try { await attendance('team-remove', { person_key: m.person_key }); load(); } catch (e) { setErr(e.message); }
  };
  if (!data) return <><Msg err={err} /><p>Loading the roster…</p></>;
  const linked = new Set(data.members.map((m) => m.person_key));
  return (
    <div>
      <p style={muted}>Who is on which team decides who sees them: Rachael sees Customer Relations; Carman, Matt Jr, Brenda and Montel see Operations. A name with "not matched" is not linked to TimeClock Plus yet; pick them from the list.</p>
      <Msg err={err} />
      <button type="button" style={{ ...btn, marginBottom: 10 }} onClick={() => setEdit({ person_key: '', person_name: '', team: 'ops', title: '', seasonal: false, admin_id: '' })}>Add a person</button>
      {edit && (
        <div style={{ ...box, padding: 10, marginBottom: 10, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={lbl}>TimeClock Plus person
            <select value={/^name:/.test(edit.person_key) ? '' : edit.person_key} onChange={(e) => {
              const emp = data.employees.find((x) => x.key === e.target.value);
              setEdit({ ...edit, person_key: e.target.value || edit.person_key, person_name: emp ? emp.name : edit.person_name });
            }} style={input}>
              <option value="">Not in TimeClock Plus</option>
              {data.employees.map((x) => <option key={x.key} value={x.key}>{x.name}{linked.has(x.key) && x.key !== edit.was ? ' (on a team)' : ''}</option>)}
            </select>
          </label>
          <label style={lbl}>Name<input value={edit.person_name} onChange={(e) => setEdit({ ...edit, person_name: e.target.value, person_key: edit.person_key || `name:${e.target.value}` })} style={input} /></label>
          <label style={lbl}>Team
            <select value={edit.team} onChange={(e) => setEdit({ ...edit, team: e.target.value })} style={input}>
              {Object.entries(me.teams).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label style={lbl}>Title<input value={edit.title || ''} onChange={(e) => setEdit({ ...edit, title: e.target.value })} style={input} /></label>
          <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center' }}><input type="checkbox" checked={!!edit.seasonal} onChange={(e) => setEdit({ ...edit, seasonal: e.target.checked })} /> Seasonal</label>
          <button type="button" style={btn} onClick={save}>Save</button>
          <button type="button" style={btn2} onClick={() => setEdit(null)}>Cancel</button>
        </div>
      )}
      {Object.entries(me.teams).map(([t, name]) => (
        <div key={t} style={{ marginBottom: 14 }}>
          <h3 style={{ fontSize: 15, margin: '6px 0' }}>{name}</h3>
          <div style={{ ...box, maxWidth: 760 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <tbody>
                {data.members.filter((m) => m.team === t).sort((a, b) => a.person_name.localeCompare(b.person_name)).map((m) => (
                  <tr key={m.person_key} style={{ borderTop: '1px solid #eef0f2' }}>
                    <td style={{ ...td, ...left, fontWeight: 600 }}>{m.person_name}{/^name:/.test(m.person_key) ? <span style={{ color: '#8a5a00', fontWeight: 400 }}> (not matched)</span> : null}</td>
                    <td style={{ ...td, ...left }}>{m.title}</td>
                    <td style={{ ...td, ...left }}>{m.seasonal ? 'Seasonal' : 'Year-round'}</td>
                    <td style={td}>
                      <button type="button" style={{ ...btn2, padding: '2px 6px', fontSize: 11 }} onClick={() => setEdit({ ...m, was: m.person_key })}>Edit</button>{' '}
                      <button type="button" style={{ ...btn2, padding: '2px 6px', fontSize: 11 }} onClick={() => remove(m)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
